import {
    ResourceAllocationState,
    PersonalAvailability,
    PersonalAvailabilityWeek,
    NonWorkingDay
} from '../../../types';

export const availabilityHandlers = {
    // ================== NON-WORKING DAY EVENTS ==================
    NonWorkingDayAdded: (
        state: ResourceAllocationState,
        data: NonWorkingDay
    ): ResourceAllocationState => {
        return {
            ...state,
            nonWorkingDays: {
                ...state.nonWorkingDays,
                [data.id]: data
            }
        };
    },
    NonWorkingDayUpdated: (
        state: ResourceAllocationState,
        data: {id: string; updates: Partial<NonWorkingDay>}
    ): ResourceAllocationState => {
        return {
            ...state,
            nonWorkingDays: {
                ...state.nonWorkingDays,
                [data.id]: {
                    ...state.nonWorkingDays[data.id],
                    ...data.updates
                }
            }
        };
    },
    NonWorkingDayDeleted: (
        state: ResourceAllocationState,
        data: {id: string}
    ): ResourceAllocationState => {
        const {[data.id]: _removed, ...rest} = state.nonWorkingDays;
        return {...state, nonWorkingDays: rest};
    },

    // ================== PERSONAL AVAILABILITY EVENTS ==================
    PersonalAvailabilityAdded: (
        state: ResourceAllocationState,
        data: PersonalAvailability & {weeks?: PersonalAvailabilityWeek[]}
    ): ResourceAllocationState => {
        // NOTE: The original code handled 'payload.weeks' as well, but standardizing on data.weeks is cleaner.
        // If the backend sends weeks in data, this works.

        const newState = {
            ...state,
            personalAvailability: {
                ...state.personalAvailability,
                [data.id]: data
            }
        };

        if (data.weeks) {
            const weeks = data.weeks;
            const newWeeks = {
                ...newState.personalAvailabilityWeeks
            };
            weeks.forEach(w => {
                newWeeks[w.id] = w;
            });
            newState.personalAvailabilityWeeks = newWeeks;
        }

        return newState;
    },
    PersonalAvailabilityUpdated: (
        state: ResourceAllocationState,
        data: {
            id: string;
            updates: Partial<PersonalAvailability>;
            weeks?: PersonalAvailabilityWeek[];
            weeksToDelete?: number[];
        }
    ): ResourceAllocationState => {
        if (data.weeks || data.weeksToDelete) {
            const weeks = data.weeks;
            const weeksToDelete = data.weeksToDelete;

            const newWeeks = {
                ...state.personalAvailabilityWeeks
            };

            if (weeksToDelete) {
                weeksToDelete.forEach(weekNum => {
                    const paIdx = data.id;
                    const pa = state.personalAvailability[paIdx];
                    if (pa) {
                        const weekIdToDelete = Object.keys(newWeeks).find(
                            weekId =>
                                newWeeks[weekId].personId === pa.personId &&
                                newWeeks[weekId].globalWeekNumber === weekNum
                        );
                        if (weekIdToDelete) delete newWeeks[weekIdToDelete];
                    }
                });
            }

            if (weeks) {
                weeks.forEach(w => {
                    newWeeks[w.id] = w;
                });
            }

            return {
                ...state,
                personalAvailability: {
                    ...state.personalAvailability,
                    [data.id]: {
                        ...state.personalAvailability[data.id],
                        ...data.updates
                    }
                },
                personalAvailabilityWeeks: newWeeks
            };
        }

        return {
            ...state,
            personalAvailability: {
                ...state.personalAvailability,
                [data.id]: {
                    ...state.personalAvailability[data.id],
                    ...data.updates
                }
            }
        };
    },
    PersonalAvailabilityDeleted: (
        state: ResourceAllocationState,
        data: {id: string}
    ): ResourceAllocationState => {
        const {[data.id]: _removed, ...rest} = state.personalAvailability;
        return {...state, personalAvailability: rest};
    },

    // ================== PERSONAL AVAILABILITY WEEK EVENTS ==================
    PersonalAvailabilityWeekAdded: (
        state: ResourceAllocationState,
        data: PersonalAvailabilityWeek
    ): ResourceAllocationState => {
        return {
            ...state,
            personalAvailabilityWeeks: {
                ...state.personalAvailabilityWeeks,
                [data.id]: data
            }
        };
    },
    PersonalAvailabilityWeekUpdated: (
        state: ResourceAllocationState,
        data: {id: string; updates: Partial<PersonalAvailabilityWeek>}
    ): ResourceAllocationState => {
        return {
            ...state,
            personalAvailabilityWeeks: {
                ...state.personalAvailabilityWeeks,
                [data.id]: {
                    ...state.personalAvailabilityWeeks[data.id],
                    ...data.updates
                }
            }
        };
    },
    PersonalAvailabilityWeekDeleted: (
        state: ResourceAllocationState,
        data: {id: string}
    ): ResourceAllocationState => {
        const {[data.id]: _removed, ...rest} = state.personalAvailabilityWeeks;
        return {...state, personalAvailabilityWeeks: rest};
    },

    // ================== SPECIAL BULK OPERATIONS ==================
    PersonalAvailabilityWeeksRangeDeleted: (
        state: ResourceAllocationState,
        data: {personId: string; weekNumbers: number[]}
    ): ResourceAllocationState => {
        const {personId, weekNumbers} = data;
        const newWeeks = {...state.personalAvailabilityWeeks};
        Object.keys(newWeeks).forEach(weekId => {
            const week = newWeeks[weekId];
            if (
                week.personId === personId &&
                weekNumbers.includes(week.globalWeekNumber)
            ) {
                delete newWeeks[weekId];
            }
        });
        return {...state, personalAvailabilityWeeks: newWeeks};
    }
};
